import React, { useEffect, useState } from 'react';
import { Users2, Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { api } from '../../lib/api';

// SupportTeamPanel — Sprint D item 3.10. Admin/ops-only. Add/remove/reorder
// the per-account support team. Defaults are seeded by migration 022; this
// panel lets the team customize who shows up on the principal's portal card.

export default function SupportTeamPanel({ accountId }) {
  const [members, setMembers] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [draft, setDraft] = useState({ name: '', role_label: '', email: '', phone: '' });

  const load = React.useCallback(() => {
    setError('');
    api.get(`/api/admin/accounts/${accountId}/support-team`)
      .then((d) => setMembers(d.members || []))
      .catch((err) => setError(err?.data?.error || err?.message || 'Failed to load'));
  }, [accountId]);
  useEffect(() => { load(); }, [load]);

  const onAdd = async (e) => {
    e?.preventDefault?.();
    if (!draft.name.trim() || !draft.role_label.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/api/admin/accounts/${accountId}/support-team`, {
        name: draft.name.trim(),
        role_label: draft.role_label.trim(),
        email: draft.email.trim() || null,
        phone: draft.phone.trim() || null,
      });
      setDraft({ name: '', role_label: '', email: '', phone: '' });
      load();
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed to add');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Remove this support team member?')) return;
    await api.delete(`/api/admin/accounts/${accountId}/support-team?memberId=${id}`);
    load();
  };

  const onReorder = async (idx, dir) => {
    const next = members.slice();
    const swapWith = idx + dir;
    if (swapWith < 0 || swapWith >= next.length) return;
    const a = next[idx]; next[idx] = next[swapWith]; next[swapWith] = a;
    setMembers(next);
    await api.patch(`/api/admin/accounts/${accountId}/support-team`, { order: next.map((m) => m.id) });
  };

  return (
    <div data-testid="support-team-panel" className="space-y-5">
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500 mb-3">
          <Users2 className="w-3 h-3" />
          Support team for this account
        </div>
        {error && <div className="text-[12px] text-red-300/90 mb-3">{error}</div>}

        {members.length === 0 && !error && (
          <div className="text-[12px] text-gray-500">No support-team members yet.</div>
        )}

        <ul className="space-y-2">
          {members.map((m, idx) => (
            <li key={m.id} data-testid={`support-team-row-${m.id}`} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/[0.04]">
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-white truncate">{m.name}</div>
                <div className="text-[11px] text-gray-500">{m.role_label}{m.email ? ` · ${m.email}` : ''}{m.phone ? ` · ${m.phone}` : ''}</div>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => onReorder(idx, -1)} className="p-1 text-gray-400 hover:text-white" title="Move up"><ChevronUp className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => onReorder(idx, +1)} className="p-1 text-gray-400 hover:text-white" title="Move down"><ChevronDown className="w-3.5 h-3.5" /></button>
                <button
                  type="button"
                  data-testid={`support-team-delete-${m.id}`}
                  onClick={() => onDelete(m.id)}
                  className="p-1 text-gray-400 hover:text-red-300"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <form onSubmit={onAdd} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-2">
          <Plus className="w-3 h-3" />
          Add member
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name *" testId="support-team-name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} />
          <Field label="Role *" testId="support-team-role" value={draft.role_label} onChange={(v) => setDraft({ ...draft, role_label: v })} />
          <Field label="Email" testId="support-team-email" value={draft.email} onChange={(v) => setDraft({ ...draft, email: v })} />
          <Field label="Phone" testId="support-team-phone" value={draft.phone} onChange={(v) => setDraft({ ...draft, phone: v })} />
        </div>
        <button
          type="submit"
          data-testid="support-team-add-submit"
          disabled={submitting || !draft.name.trim() || !draft.role_label.trim()}
          className="px-4 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] disabled:opacity-50"
        >
          {submitting ? 'Adding…' : 'Add member'}
        </button>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1">{label}</span>
      <input
        type="text"
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white"
      />
    </label>
  );
}
